'use client'

import Link from 'next/link'
import '@/components/reference/blog/blog.css'
import '@/components/reference/content-pages.css'

interface SiteErrorProps {
  error: Error & { digest?: string }
  reset: () => void
  title: string
  message: string
  /**
   * Ways out beside "Try again", drawn as ghost buttons. `reload` leaves by a
   * full page load instead of client navigation: the root boundary uses it,
   * since the error may have left the app shell itself in a bad state.
   */
  links: Array<{ href: string; label: string; reload?: boolean }>
  /** Adds the "Contact our support team" line. */
  help?: boolean
}

/**
 * The error-boundary page in the reference shell (design overhaul), the same
 * hero layout as the 404 (SiteNotFound). Presentational only: each route's
 * error.tsx keeps its own reporting (Sentry or console) and its own copy.
 */
export default function SiteError({
  error,
  reset,
  title,
  message,
  links,
  help = false,
}: SiteErrorProps) {
  return (
    <div className='shell pg-blog cp'>
      <div className='sheet bhero bpage cp-last'>
        <div className='container'>
          <div className='cp-nf'>
            <span className='eyebrow'>Error</span>
            <h1 className='h-display h1'>{title}</h1>
            <p className='lede'>{message}</p>
            <div className='hero__cta'>
              <button type='button' className='btn' onClick={reset}>
                Try again
              </button>
              {links.map(link =>
                link.reload ? (
                  <button
                    key={link.href}
                    type='button'
                    className='btn btn--ghost'
                    onClick={() => window.location.assign(link.href)}
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    className='btn btn--ghost'
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
            {help && (
              <p className='cp-nf__help'>
                Still having issues?{' '}
                <Link href='/contact-us'>Contact our support team</Link>
              </p>
            )}
            {process.env.NODE_ENV === 'development' && (
              <details className='cp-nf__dev'>
                <summary>Error details (development only)</summary>
                <pre>
                  {error.message}
                  {error.digest && `\nDigest: ${error.digest}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
